/**
 * useSyncEngine — React-side mount/dispose for the web sync engine.
 *
 * Pairs with `syncEngineProvider.ts`:
 *   - Constructs the engine when an authenticated session is present.
 *   - Attaches the periodic + visibility lifecycle.
 *   - Hosts the SSE subscriber (T12.0.17 Phase 2) and routes inbound events
 *     through `engine.applyOne` so the projection path matches `pull()`.
 *   - Tears everything down when the session disappears (logout) or the
 *     component unmounts.
 *
 * The hook is mounted inside the auth-guarded subtree so it never runs when
 * the user is logged-out. `useTokens()` + `useMe()` already gate auth, and
 * the hook reads the same user object so identity stays consistent with the
 * rest of the app.
 *
 * The status-indicator UI subscribes to the same lifecycle hook via
 * `usePullState` so it can show "Updating…" during catch-up pulls + when the
 * SSE connection is down (D5).
 */

import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useMe } from '@shopista/shared/api/hooks';
import type { SyncEngine, SyncIdentity } from '@shopista/shared/sync';
import { useTokens } from '../auth/useTokens';
import {
  attachEngineLifecycle,
  createWebSyncEngine,
  getOrCreateDeviceId,
  type EngineLifecycleHandle,
  type PullLifecycleState,
} from './syncEngineProvider';
import {
  attachSseSubscriber,
  type SseConnectionState,
  type SseSubscriberHandle,
} from './sseSubscriber';

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3001';

export interface SyncEngineStatus {
  /** Engine instance — null until auth identity resolves. */
  engine: SyncEngine | null;
  /** Last pull lifecycle state. */
  pullState: PullLifecycleState;
  /** Current SSE connection state. */
  sseState: SseConnectionState;
}

/**
 * Mount the sync engine for the currently-authenticated user.
 *
 * Should be called inside the protected subtree (after `AuthGuard`). Safe to
 * call once per app boot — the hook handles auth changes (user-id change,
 * logout) internally.
 */
export function useSyncEngine(): SyncEngineStatus {
  const tokens = useTokens();
  const meQuery = useMe();
  const queryClient = useQueryClient();

  const [engine, setEngine] = useState<SyncEngine | null>(null);
  const [pullState, setPullState] = useState<PullLifecycleState>('idle');
  const [sseState, setSseState] = useState<SseConnectionState>('disconnected');

  // Hold the lifecycle + SSE handles in refs so the effect cleanup can
  // dispose them deterministically (state setters would race the unmount).
  const lifecycleRef = useRef<EngineLifecycleHandle | null>(null);
  const sseRef = useRef<SseSubscriberHandle | null>(null);

  const user = meQuery.data;
  const userId = user?.id ?? null;
  const householdId = user?.householdId ?? null;

  useEffect(() => {
    if (!userId || !householdId) {
      // Logged-out / hydrating. Tear down any prior engine.
      lifecycleRef.current?.dispose();
      sseRef.current?.dispose();
      lifecycleRef.current = null;
      sseRef.current = null;
      setEngine(null);
      setPullState('idle');
      setSseState('disconnected');
      return;
    }

    const deviceId = getOrCreateDeviceId();
    const identity: SyncIdentity = { householdId, userId, deviceId };

    const { engine: localEngine, refresh } = createWebSyncEngine({
      apiBaseUrl: API_BASE_URL,
      tokens,
      queryClient,
    });

    const lifecycle = attachEngineLifecycle({
      engine: localEngine,
      identity: () => identity,
      onPullStateChange: setPullState,
    });

    // Phase 2 — SSE subscriber. Hands every inbound event through
    // `engine.applyOne` so the projection path matches `pull()`. The
    // subscriber owns its own lifecycle including tab-return reopen +
    // immediate pull (per D3).
    const sse = attachSseSubscriber({
      apiBaseUrl: API_BASE_URL,
      tokens,
      // T12.0.23 D3 — the subscriber awaits this before every (re)connect so
      // the access token is refreshed through the shared deduped path; closes
      // the past-TTL 401 reconnect loop.
      refresh,
      engine: localEngine,
      identity: () => identity,
      // Tab-return triggers an immediate pull through the lifecycle handle
      // so the catch-up cursor advances even when the connection was up the
      // whole time. Reason `'tab-return'` lights the indicator (D5) until
      // the pull lands.
      onTabReturn: () => {
        void lifecycle.pullNow('tab-return');
      },
      onStateChange: setSseState,
    });

    lifecycleRef.current = lifecycle;
    sseRef.current = sse;
    setEngine(localEngine);

    // Initial seed pull so a fresh page load doesn't wait the full 30 s
    // interval to bring local storage in line with the server. Reason
    // `'mount'` lights the indicator (D5) while the seed catches up.
    void lifecycle.pullNow('mount');

    return () => {
      lifecycle.dispose();
      sse.dispose();
      lifecycleRef.current = null;
      sseRef.current = null;
    };
  }, [userId, householdId, tokens, queryClient]);

  return { engine, pullState, sseState };
}
